import React, { useEffect, useState } from 'react';
import { Province, Town, fetchTowns } from '../api/locations';

interface BusinessInformationEditableProps {
  contactPerson: string;
  onContactPersonChange: (person: string) => void;
  contactPersonError?: string;
  phone: string;
  onPhoneChange: (phone: string) => void;
  email: string;
  onEmailChange: (email: string) => void;
  address: string;
  onAddressChange: (address: string) => void;
  addressError?: string;
  province: string;
  onProvinceChange: (province: string) => void;
  provinceError?: string;
  city: string;
  onCityChange: (city: string) => void;
  cityError?: string;
  postalCode: string;
  onPostalCodeChange: (code: string) => void;
  postalCodeError?: string;
  website: string;
  onWebsiteChange: (website: string) => void;
  provinces: Province[];
}

const BusinessInformationEditable: React.FC<BusinessInformationEditableProps> = ({
  contactPerson,
  onContactPersonChange,
  contactPersonError,
  phone,
  onPhoneChange,
  email,
  onEmailChange,
  address,
  onAddressChange,
  addressError,
  province,
  onProvinceChange,
  provinceError,
  city,
  onCityChange,
  cityError,
  postalCode,
  onPostalCodeChange,
  postalCodeError,
  website,
  onWebsiteChange,
  provinces,
}) => {
  const [towns, setTowns] = useState<Town[]>([]);

  const selectedProvince = provinces.find(p => p.name === province);

  useEffect(() => {
    if (selectedProvince) {
      fetchTowns(selectedProvince.id).then(setTowns).catch(() => setTowns([]));
    } else {
      setTowns([]);
    }
  }, [selectedProvince?.id]);

  const handleProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const name = e.target.value;
    onProvinceChange(name);
    onCityChange('');
  };

  return (
    <section className="mb-8 border-b pb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Business Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-person" className="block text-sm font-medium text-gray-700">
            Contact Person <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="contact-person"
            className={`mt-1 block w-full p-2 border rounded-md ${contactPersonError ? 'border-red-500' : 'border-gray-300'}`}
            value={contactPerson}
            onChange={(e) => onContactPersonChange(e.target.value)}
          />
          {contactPersonError && <p className="text-red-500 text-xs mt-1">{contactPersonError}</p>}
        </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone</label>
          <input
            type="tel"
            id="phone"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            value={phone}
            onChange={(e) => onPhoneChange(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            id="email"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="website" className="block text-sm font-medium text-gray-700">Website</label>
          <input
            type="url"
            id="website"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            value={website}
            onChange={(e) => onWebsiteChange(e.target.value)}
          />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="address" className="block text-sm font-medium text-gray-700">
            Street Address <span className="text-red-500">*</span>
          </label>
          <textarea
            id="address"
            rows={3}
            className={`mt-1 block w-full p-2 border rounded-md ${addressError ? 'border-red-500' : 'border-gray-300'}`}
            value={address}
            onChange={(e) => onAddressChange(e.target.value)}
          />
          {addressError && <p className="text-red-500 text-xs mt-1">{addressError}</p>}
        </div>
        <div>
          <label htmlFor="province" className="block text-sm font-medium text-gray-700">
            Province <span className="text-red-500">*</span>
          </label>
          <select
            id="province"
            className={`mt-1 block w-full p-2 border rounded-md ${provinceError ? 'border-red-500' : 'border-gray-300'}`}
            value={province}
            onChange={handleProvinceChange}
          >
            <option value="">Select province</option>
            {provinces.map(p => (
              <option key={p.id} value={p.name}>{p.name}</option>
            ))}
          </select>
          {provinceError && <p className="text-red-500 text-xs mt-1">{provinceError}</p>}
        </div>
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700">
            City / Town <span className="text-red-500">*</span>
          </label>
          <select
            id="city"
            className={`mt-1 block w-full p-2 border rounded-md ${cityError ? 'border-red-500' : 'border-gray-300'}`}
            value={city}
            onChange={(e) => onCityChange(e.target.value)}
            disabled={!selectedProvince}
          >
            <option value="">Select city / town</option>
            {towns.map(t => (
              <option key={t.id} value={t.name}>{t.name}</option>
            ))}
          </select>
          {cityError && <p className="text-red-500 text-xs mt-1">{cityError}</p>}
        </div>
        <div>
          <label htmlFor="postal-code" className="block text-sm font-medium text-gray-700">
            Postal Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="postal-code"
            className={`mt-1 block w-full p-2 border rounded-md ${postalCodeError ? 'border-red-500' : 'border-gray-300'}`}
            value={postalCode}
            onChange={(e) => onPostalCodeChange(e.target.value)}
          />
          {postalCodeError && <p className="text-red-500 text-xs mt-1">{postalCodeError}</p>}
        </div>
      </div>
    </section>
  );
};

export default BusinessInformationEditable;
